<script setup>
  import { defineEmits, ref, inject, watch, computed } from 'vue'
  import Footer from '../FooterSection.vue'
  import { useQuestionsStore } from '../../stores/QuestionsStore'
  import { storeToRefs } from 'pinia'
  import { useForm } from 'vee-validate'
  import { toTypedSchema } from '@vee-validate/yup'
  import { object, string, date } from 'yup'
  import BcsDynamicText from '../BcsDynamicText.vue'
  import PlanQuestionnaireForm from './PlanQuestionnaireForm.vue'
  import { useEmployerStore } from '../../stores/EmployerStore'
  import { RequestService } from '../../services/RequestService'
  import PlanYearDropdown from '@/shared/components/common/PlanYearDropdown.vue'
  import AlertNotification from '../AlertNotification.vue'
  import { useReportsStore } from '../../stores/ReportsStore'
  import { AgencyDataService } from '../../services/AgencyDataService'
  import { derivePlanPdfFields } from '../../utils/questionsAndAnswersHelpers'

  const props = defineProps({
    showNewPlanYearNotification: {
      type: Boolean,
      default: false
    }
  })

  const { createLog, handleErrors } = RequestService()
  const { markAgencyDataUpdated } = AgencyDataService()

  const emit = defineEmits(['closeNewPlanYearNotification'])

  const customNavigation = inject('customNavigation')

  const questionStore = useQuestionsStore()
  const reportsStore = useReportsStore()
  const { planQuestions, mainAssessKey } = storeToRefs(questionStore)

  const { saveQuestions, isPlanComplete, clearQuestions } = questionStore

  const {
    defineField,
    setFieldValue,
    setFieldTouched,
    isFieldValid,
    isFieldTouched,
    errors,
    validate
  } = useForm({
    validationSchema: toTypedSchema(
      object({
        name: string().min(4).max(150).required(),
        ein: string()
          .required()
          .matches(/^[0-9]{2}-[0-9]{7}$/),
        plan_start_date: date()
          .typeError('Please enter a valid date')
          .required()
          .test('is-date-complete', 'Invalid date format', () => {
            if (!plan_start_date.value) return true
            const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/g
            return dateRegex.test(plan_start_date.value)
          })
      })
    )
  })

  const employerStore = useEmployerStore()
  const { actualEmployer, actualPlanYear } = storeToRefs(employerStore)

  const [plan_start_date] = defineField('plan_start_date')

  watch(
    () => actualEmployer?.value?.name,
    (newVal) => {
      setFieldValue('name', newVal)
      setFieldTouched('name', true)
      validate()
    },
    { immediate: true }
  )

  watch(
    () => actualEmployer?.value?.ein,
    (newVal) => {
      setFieldValue('ein', newVal)
      setFieldTouched('ein', true)
      validate()
    },
    { immediate: true }
  )

  watch(
    () => actualPlanYear?.value?.plan_start_date,
    (newVal) => {
      setFieldValue('plan_start_date', newVal)
      setFieldTouched('plan_start_date', true)
      validate()
    },
    { immediate: true }
  )

  // Push employer basics into cache store
  watch(
    [() => actualEmployer?.value, () => actualPlanYear?.value],
    ([emp, plan]) => {
      if (!emp || !plan) return
      reportsStore.setEmployerInfo({
        name: emp.name,
        ein: emp.ein,
        planYear: plan.plan_start_date,
        planYearId: plan.id,
        groupInsuranceType: plan.groupInsuranceType,
      })
    },
    { immediate: true, deep: true }
  )

  // Derive most-used fields for PDFs from plan questions and cache them
  function updateReportsPlanDerived() {
    try {
      const root = Object.values(planQuestions.value || {})[0]
      if (!root) return
      const derived = {
        ...derivePlanPdfFields(root),
        isPlanComplete: isPlanComplete(),
      }
      reportsStore.setPlanDerived(derived)
    } catch (_) {}
  }

  watch(
    [() => planQuestions.value, () => mainAssessKey.value],
    () => updateReportsPlanDerived(),
    { immediate: true, deep: true }
  )

  const hasErrors = computed(() => {
    return Object.keys(errors.value).length > 0
  })

  const saving = ref(false)

  const lastTimeSavedPlan = ref(null)
  const timeElapsedPlan = ref('Not auto-saved yet ')
  const idSetIntervalSecondPlan = ref(null)
  const idSetIntervalMinutePlan = ref(null)

  async function autoSavePlan() {
    // Ensure the schema is valid before saving the draft automatically
    const isValid = await validate()
    if (!isValid || hasErrors.value) {
      // Form is not valid or required initial fields are missing; skip autosave
      return
    }

    savePlan()
    lastTimeSavedPlan.value = new Date()
    timeElapsedPlan.value = 'Auto-saved just now'

    if (idSetIntervalSecondPlan.value) {
      clearInterval(idSetIntervalSecondPlan.value)
    }
    if (idSetIntervalMinutePlan.value) {
      clearInterval(idSetIntervalMinutePlan.value)
    }
    let countSeconds = 0
    idSetIntervalSecondPlan.value = setInterval(() => {
      countSeconds++
      let now = new Date()
      let diff = now - lastTimeSavedPlan.value
      let seconds = Math.floor(diff / 1000)
      timeElapsedPlan.value = `Auto-saved ${seconds} secs ago`
      if (countSeconds > 59) {
        clearInterval(idSetIntervalSecondPlan.value)
        idSetIntervalMinutePlan.value = setInterval(() => {
          let now = new Date()
          let diff = now - lastTimeSavedPlan.value
          let minutes = Math.floor(diff / 60000)
          timeElapsedPlan.value = `Auto-saved ${minutes} mins ago`
        }, 60000)
      }
    }, 10000)
  }

  async function savePlan() {
    //first we update the employer name, ein or compliance date
    saving.value = true
    try {
      await saveQuestions('plan')
      // Mark agency data as updated for Action Center cache invalidation
      markAgencyDataUpdated(actualEmployer.value.agency_id)
      // After persisting questions, update saved copy for dropdown label
      const employerStore = useEmployerStore()
      employerStore.changePlanStartDateCopy(actualPlanYear.value?.plan_start_date)
      //once we are done, we change the page
      saving.value = false
      const PLAN_QUESTIONNAIRE_UPDATED = '7'
      await createLog({
        eventId: PLAN_QUESTIONNAIRE_UPDATED,
        logName: 'user_activity_log_v2',
        employerId: actualEmployer.value.id,
        planYearId: actualPlanYear.value.id,
        agencyId: actualEmployer.value.agency_id
      })
    } catch (error) {
      handleErrors('savePlan', error)
    }
  }

  function startAssessment() {
    customNavigation.value.navigate({
      newPage: customNavigation.value.currentPage,
      subPage: customNavigation.value.routes.DetailEmployer.children[2].name
    })
  }

  function answeredQuestions() {
    /*if (initialAnswers.value && initialAnswers.value) {
      return Object.entries(initialAnswers.value).filter(
        (val) => typeof val[1] === 'number' || (val[1] && val[1].length > 0)
      ).length
    }*/
    return isPlanComplete()
  }

  async function cleanInitialForm() {
    clearQuestions('plan')
    await saveQuestions('plan')
    // Mark agency data as updated for Action Center cache invalidation
    markAgencyDataUpdated(actualEmployer.value.agency_id)
  }

  function isInitialDataComplete() {
    if (
      actualEmployer.value.name &&
      actualEmployer.value.name.length > 3 &&
      actualEmployer.value.ein &&
      actualEmployer.value.ein.length === 10 &&
      actualPlanYear.value?.plan_start_date &&
      actualPlanYear.value.plan_start_date.length === 10
    ) {
      return true
    }
    return false
  }

</script>

<template>
  <div class="col-10 scroll-fix pt-3.5">
    <div>
      <div class="px-2.5 pt-3" v-if="props.showNewPlanYearNotification">
        <AlertNotification
          :showCloseButton="true"
          typeAlert="info"
          @update:showAlert="emit('closeNewPlanYearNotification')"
          class="mb-2 text-14">
          <strong>
            <BcsDynamicText text="planQuestionnaireNewPlanYearAlertText"/>
          </strong>
        </AlertNotification>
      </div>
      <form @submit.prevent>
        <!-- Form header -->
        <div class="container-fluid px-3">
          <div class="row">
            <div class="col-7">
              <div class="mt-3">
                <div class="d-flex">
                  <span class="text-secondary text-28 me-2">
                    <med>
                      {{ actualEmployer.name }} Plan Questionnaire
                    </med>
                  </span>
                  <PlanYearDropdown />
                </div>
              </div>
              <div class="mt-1">
                <span class="text-12">
                  <BcsDynamicText text="planQuestionnaireInstructionalText" />
                </span>
              </div>
              <div class="mt-2.5">
                <span class="text-12">
                  <strong>
                    (All fields are required unless specified optional.)
                  </strong>
                </span>
              </div>
            </div>
            <div class="col-5">
              <div class="d-flex justify-content-end">
                <!-- <a
                  class="hand-cursor d-inline my-3 px-2 border-end text-decoration-none text-14 text-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#pendingFunctionModal">
                  <strong>View Sample Report</strong>
                </a> -->
              </div>
              <div class="d-grid gap-1 col-4 float-end mt-3 w-100">
                <button
                  id="saveDraftButton"
                  @click="savePlan()"
                  class="btn btn-primary text-16 ms-auto"
                  :disabled="saving || !isInitialDataComplete() || hasErrors">
                  <i class="bi bi-bookmark"></i>
                  <med>{{ saving ? ' Saving...' : ' Save Draft' }}</med>
                </button>
                <span class="text-12 text-end">{{ timeElapsedPlan }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="container-fluid px-3 mt-3">

          <!-- Integrated PlanQuestionnaireForm component -->
          <PlanQuestionnaireForm
            :questions="planQuestions[mainAssessKey]"
            :autoSave="true"
            :isRenewPlanYearForm="false"
            :employerData="actualEmployer"
            :planYearData="actualPlanYear"
            :isFieldValid="isFieldValid"
            :isFieldTouched="isFieldTouched"
            @save="autoSavePlan"
            @validate="validate"
          />
        </div>

        <hr />
        <div class="d-grid gap-2 d-md-block text-center">
          <button
            id="clearFormButtonPlan"
            type="button"
            :disabled="answeredQuestions() === 0"
            @click="cleanInitialForm"
            class="btn btn-info me-2">
            <i class="bi bi-x-circle"></i>
            Clear Form
          </button>
          <button
            id="startAssessmentButtonPlan"
            type="button"
            :disabled="!isPlanComplete() || !isInitialDataComplete() || hasErrors"
            class="btn btn-primary ms-2"
            @click="startAssessment()">
            Start Assessment
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>
        <div class="row d-flex mt-3 1">
          <div class="col-12 p-0">
            <Footer class="px-4"></Footer>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .form-check-input {
    border: 1px solid var(--bs-gray-600, #6c757d) !important;
  }

  .question-title {
    flex-basis: 100%;
  }

  .floating-draft {
    position: relative;
    right: -15.5vw;
  }

  .steps-banner {
    background-color: white;
  }

  .wizard-container {
    padding: 0 10px;
    display: flex;
    justify-content: space-between;
    position: relative;
  }

  .step-container {
    width: 10%;
  }

  .circle {
    line-height: 45px;
    -moz-border-radius: 50%;
    border: solid 3px;
    border-color: var(--bs-primary);
    background-color: #ffffff;
    color: var(--bs-primary);
    text-align: center;
    font-size: 20px;

    width: 30px; /* Adjust circle size */
    height: 30px; /* Adjust circle size */
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: bold;
  }

  .circle.filled {
    background-color: var(--bs-dark);
    color: white;
    border: 2px solid var(--bs-dark);
  }

  .circle.active {
    box-shadow: 0 0 0 4px #7b61ff78;
  }

  .empty {
    div {
      background-color: #ffffff; /* Adjust empty circle color */
      border: 2px solid var(--bs-secondary); /* Adjust empty circle border color */
      i {
        visibility: hidden;
      }
    }
    p {
      color: var(--bs-secondary);
    }
  }

  .partial {
    div {
      box-shadow:
        inset 0 0 0 8px #ffffff,
        inset 0 0 0 80px #007bff;
      i {
        visibility: hidden;
      }
    }
  }

  .scroll-padding {
    scroll-padding-top: 13vh;
    scroll-margin-top: 13vh;
  }

  .scroll-fix {
    overflow-y: scroll;
    overflow-x: hidden;
    max-height: 94vh;
  }

  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield;
  }
</style>
